import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import { useBackupManagement } from './hooks/useBackupManagement';

interface Backup {
  filename: string;
  path: string;
  size: number;
  sizeFormatted: string;
  created: Date | string;
  modified: Date | string;
  userId: number;
  username: string;
}

interface User {
  id: number;
  username: string;
}

const BackupManagement: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const isEmbedded = embedded || new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('embed') === '1';

  // Vérifier l'accès autorisé (rôles 1, 2, 3, 4)
  const hasAccess = currentUser?.roles_id && [1, 2, 3, 4].includes(currentUser.roles_id);

  const [activeTab, setActiveTab] = useState<'complete' | 'user'>('complete');

  // État + actions extraits dans hooks/useBackupManagement
  const {
    searchTerm,
    setSearchTerm,
    searchResults,
    setSearchResults,
    selectedUser,
    setSelectedUser,
    searching,
    backups,
    setBackups,
    loadingBackups,
    backupsDir,
    setBackupsDir,
    selectedBackup,
    setSelectedBackup,
    alert,
    setAlert,
    deleteConfirm,
    setDeleteConfirm,
    loadUserBackups,
    deleteBackup,
    confirmDelete,
    downloadBackup,
  } = useBackupManagement();

  // Charger les sauvegardes quand un joueur est sélectionné
  useEffect(() => {
    if (selectedUser && activeTab === 'user') {
      loadUserBackups(selectedUser.id);
    } else {
      setBackups([]);
      setBackupsDir('');
      setSelectedBackup(null);
    }
  }, [selectedUser, activeTab, loadUserBackups, setBackups, setBackupsDir, setSelectedBackup]);
  
  if (!hasAccess) {
    return null;
  }

  return (
    <div className={!isEmbedded ? 'min-h-screen relative overflow-hidden' : ''}>
      {!isEmbedded && <BackgroundImage />}
      {!isEmbedded && <ParticlesBackground />}
      {!isEmbedded && <Navbar />}
      <div className={!isEmbedded ? 'relative z-10 pt-24 pb-16' : ''}>
        <div className={!isEmbedded ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : ''}>
          {!isEmbedded && (
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                Gestion des Sauvegardes
              </h1>
              <p className="text-gray-300">
                Créez, restaurez et gérez les sauvegardes de la base de données
              </p>
              {backupsDir && (
                <p className="text-sm text-gray-400 mt-2">Dossier de sauvegarde : {backupsDir}</p>
              )}
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour au panel</span>
            </button>
          </div>
          )}
          
          {/* Barre de recherche de joueur */}
          {activeTab === 'user' && (
            <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-4 backdrop-blur-sm mb-4">
              <div className="relative">
                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                  Rechercher un joueur par pseudo ou ID
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Tapez le pseudo ou l'ID du joueur..."
                    className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 pl-9 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60"
                  />
                  <svg
                    className="absolute left-3 top-2.5 w-4 h-4 text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searching && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#3d4fdb]"></div>
                    </div>
                  )}
                </div>

                {searchResults.length > 0 && !selectedUser && (
                  <div className="mt-2 rounded-lg border border-[#2a3a62] bg-[#0a132b] shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => {
                          setSelectedUser(user);
                          setSearchTerm(user.username);
                          setSearchResults([]);
                        }}
                        className="px-4 py-3 hover:bg-[#0d1b3a] cursor-pointer transition-colors border-b border-[#1e2d4d] last:border-b-0"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-slate-100 font-medium">{user.username}</span>
                          <span className="text-sm text-slate-400">ID: {user.id}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {selectedUser && (
                  <div className="mt-3 p-3 rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/10 flex items-center justify-between">
                    <div>
                      <span className="text-slate-100 font-semibold">{selectedUser.username}</span>
                      <span className="text-sm text-slate-400 ml-2">(ID: {selectedUser.id})</span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedUser(null);
                        setSearchTerm('');
                        setSearchResults([]);
                      }}
                      className="text-slate-400 hover:text-white transition-colors"
                      title="Désélectionner"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Onglets */}
          <div className="mb-4 border-b border-[#1e2d4d]">
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setActiveTab('complete');
                  setSelectedUser(null);
                  setSearchTerm('');
                  setSearchResults([]);
                }}
                className={`px-4 py-2 text-sm font-semibold transition-colors ${
                  activeTab === 'complete'
                    ? 'text-[#7b93f5] border-b-2 border-[#3d4fdb]'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                Sauvegardes complètes
              </button>
              <button
                onClick={() => setActiveTab('user')}
                className={`px-4 py-2 text-sm font-semibold transition-colors ${
                  activeTab === 'user'
                    ? 'text-[#7b93f5] border-b-2 border-[#3d4fdb]'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Sauvegardes utilisateur
                {selectedUser && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                    {selectedUser.username}
                  </span>
                )}
              </button>
            </div>
          </div>
          
          {/* Alertes */}
          {alert && (
            <div className={`mb-4 p-4 rounded-lg ${
              alert.type === 'success' 
                ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                : 'bg-red-500/20 border border-red-500/50 text-red-400'
            }`}>
              <div className="flex items-center justify-between">
                <span>{alert.message}</span>
                <button
                  onClick={() => setAlert(null)}
                  className="ml-4 text-current hover:opacity-70"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Boîte de dialogue de confirmation de suppression */}
          {deleteConfirm && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="rounded-xl border border-[#243052]/90 bg-[#0a1128] p-6 max-w-md w-full shadow-2xl">
                <h3 className="text-lg font-bold text-white mb-4">Confirmer la suppression</h3>
                <p className="text-slate-300 mb-6 text-sm">
                  Êtes-vous sûr de vouloir supprimer la sauvegarde <strong className="text-white">{deleteConfirm.filename}</strong> ?
                  <br />
                  <span className="text-sm text-slate-500 mt-2 block">Cette action est irréversible.</span>
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-black/45 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="rounded-lg border border-red-500/35 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-200 hover:bg-red-500/20 transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contenu selon l'onglet */}
          <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-4 backdrop-blur-sm">
            {activeTab === 'complete' ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 mx-auto mb-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-slate-400 mb-2">Sauvegardes complètes</p>
                <p className="text-slate-500 text-sm">Cette fonctionnalité sera disponible prochainement</p>
              </div>
            ) : (
              <div>
                {!selectedUser ? (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto mb-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-slate-400 mb-2">Recherchez un joueur pour voir ses sauvegardes</p>
                    <p className="text-slate-500 text-sm">Utilisez la barre de recherche ci-dessus pour trouver un joueur</p>
                  </div>
                ) : loadingBackups ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#3d4fdb]"></div>
                    <p className="mt-4 text-slate-400 text-sm">Chargement des sauvegardes...</p>
                  </div>
                ) : backups.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto mb-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-slate-400 mb-2">Aucune sauvegarde trouvée</p>
                    <p className="text-slate-500 text-sm">Ce joueur n'a pas encore de sauvegardes dans son dossier</p>
                    {backupsDir && <p className="text-xs text-slate-600 mt-2">Dossier : {backupsDir}</p>}
                  </div>
                ) : (
                  <div>
                    {backupsDir && (
                      <div className="mb-4 rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 px-3 py-2">
                        <p className="text-sm text-slate-400">
                          <span className="font-medium text-slate-300">Dossier :</span> {backupsDir}
                        </p>
                      </div>
                    )}
                    <div className="space-y-3">
                      {backups.map((backup) => (
                        <div
                          key={backup.filename}
                          className={`rounded-lg border p-4 transition-all ${
                            selectedBackup === backup.filename
                              ? 'border-[#3d4fdb]/60 bg-[#3d4fdb]/10'
                              : 'border-[#2b3a61] bg-[#0d1b3a]/80 hover:border-[#3d4fdb]/30'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <input
                                  type="radio"
                                  checked={selectedBackup === backup.filename}
                                  onChange={() => setSelectedBackup(backup.filename)}
                                  className="w-4 h-4"
                                />
                                <span className="font-medium text-slate-100 text-sm">{backup.filename}</span>
                              </div>
                              <div className="ml-7 flex flex-wrap gap-4 text-xs text-slate-400">
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                                  </svg>
                                  {backup.sizeFormatted}
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  Créé : {new Date(backup.created).toLocaleString('fr-FR')}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => downloadBackup(backup.userId, backup.filename)}
                                className="p-1.5 text-sky-400 hover:bg-sky-500/20 rounded transition-colors"
                                title="Télécharger"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                              <button
                                onClick={() => deleteBackup(backup.userId, backup.filename)}
                                className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                                title="Supprimer"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupManagement;

